import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap, catchError } from 'rxjs';
import { AdminUser } from '../interface/admin-user.interface';
import { ConfigService } from 'src/app/services/config.service';
import { clearRumUser, identifyRumUser } from 'src/app/shared/utils/rum-user.util';

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_USER_KEY = 'admin_user';
const REFRESH_MARGIN_MS = 5 * 60 * 1000; // 5 minutes before expiry

@Injectable({ providedIn: 'root' })
export class AdminAuthService implements OnDestroy {
  private adminUser$ = new BehaviorSubject<AdminUser | null>(this.loadStoredUser());
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  private refreshIframe: HTMLIFrameElement | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private refreshInFlight: Promise<void> | null = null;
  private visibilityListener: (() => void) | null = null;

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private ngZone: NgZone
  ) {
    if (this.isAuthenticated()) {
      this.scheduleTokenRefresh();
    }
    this.setupVisibilityListener();
  }

  ngOnDestroy(): void {
    this.cleanupRefresh();
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
  }

  getMe(): Observable<AdminUser> {
    return this.http.get<AdminUser>(this.config.urlAdminAuthMe).pipe(
      tap(user => {
        this.adminUser$.next(user);
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
        this.identifyInRum(user);
      }),
      catchError(err => {
        this.logout();
        throw err;
      })
    );
  }

  get currentUser$(): Observable<AdminUser | null> {
    return this.adminUser$.asObservable();
  }

  get currentUser(): AdminUser | null {
    return this.adminUser$.value;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  getToken(): string | null {
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    this.scheduleTokenRefresh();
  }

  logout(): void {
    this.cleanupRefresh();
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    this.adminUser$.next(null);
    clearRumUser();
  }

  scheduleTokenRefresh(): void {
    this.clearRefreshTimer();
    const token = this.getToken();
    if (!token) return;

    const expiryMs = this.getTokenExpiry(token);
    if (expiryMs <= 0) return;

    const delay = expiryMs - Date.now() - REFRESH_MARGIN_MS;
    if (delay <= 0) {
      this.silentRefresh().catch(() => this.logout());
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.refreshTimerId = setTimeout(() => {
        this.ngZone.run(() => this.silentRefresh().catch(() => this.logout()));
      }, delay);
    });
  }

  silentRefresh(): Promise<void> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this._doSilentRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private _doSilentRefresh(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.cleanupIframe();

      const redirectUri = this.config.cognitoRedirectUri;
      const params = new URLSearchParams({
        client_id: this.config.clientId,
        response_type: 'token',
        scope: 'openid email profile',
        redirect_uri: redirectUri,
        identity_provider: 'Google',
        prompt: 'none'
      });

      const authUrl = `${this.config.cognitoDomain}/oauth2/authorize?${params.toString()}`;
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');

      const timeoutId = setTimeout(() => {
        this.cleanupIframe();
        reject(new Error('Silent refresh timed out'));
      }, 10000);

      this.messageListener = (event: MessageEvent) => {
        if (!event.origin || !redirectUri.startsWith(event.origin)) return;

        const fragment = event.data?.fragment || event.data;
        if (typeof fragment !== 'string') return;

        const urlParams = new URLSearchParams(fragment);
        const newToken = urlParams.get('id_token') || urlParams.get('access_token');

        if (newToken) {
          clearTimeout(timeoutId);
          this.cleanupIframe();
          localStorage.setItem(ADMIN_TOKEN_KEY, newToken);
          this.scheduleTokenRefresh();
          resolve();
        }
      };

      window.addEventListener('message', this.messageListener);

      iframe.onload = () => {
        try {
          const iframeUrl = iframe.contentWindow?.location.href;
          if (iframeUrl && iframeUrl.startsWith(redirectUri)) {
            const hash = iframe.contentWindow?.location.hash?.substring(1);
            if (hash) {
              const urlParams = new URLSearchParams(hash);
              const newToken = urlParams.get('id_token') || urlParams.get('access_token');
              if (newToken) {
                clearTimeout(timeoutId);
                this.cleanupIframe();
                localStorage.setItem(ADMIN_TOKEN_KEY, newToken);
                this.scheduleTokenRefresh();
                resolve();
                return;
              }
            }
          }
        } catch {
          // Cross-origin — wait for postMessage
        }
      };

      iframe.onerror = () => {
        clearTimeout(timeoutId);
        this.cleanupIframe();
        reject(new Error('Silent refresh iframe failed'));
      };

      iframe.src = authUrl;
      document.body.appendChild(iframe);
      this.refreshIframe = iframe;
    });
  }

  isTokenNearExpiry(marginMs: number = REFRESH_MARGIN_MS): boolean {
    const token = this.getToken();
    if (!token) return true;
    const expiry = this.getTokenExpiry(token);
    return expiry <= 0 || expiry - Date.now() < marginMs;
  }

  getTokenExpiry(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return (payload.exp || 0) * 1000;
    } catch {
      return 0;
    }
  }

  private isTokenExpired(token: string): boolean {
    const expiry = this.getTokenExpiry(token);
    return expiry <= 0 || expiry < Date.now();
  }

  private cleanupRefresh(): void {
    this.clearRefreshTimer();
    this.cleanupIframe();
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  private cleanupIframe(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    if (this.refreshIframe) {
      this.refreshIframe.remove();
      this.refreshIframe = null;
    }
  }

  private setupVisibilityListener(): void {
    this.visibilityListener = () => {
      if (document.visibilityState === 'visible' && this.getToken()) {
        if (this.isTokenNearExpiry()) {
          this.silentRefresh().catch(() => this.logout());
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);
  }

  private identifyInRum(user: AdminUser): void {
    identifyRumUser({
      email: user.email,
      userType: 'admin',
      role: user.role,
    });
  }

  private loadStoredUser(): AdminUser | null {
    try {
      const stored = localStorage.getItem(ADMIN_USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }
}
